// Components==============
import { AnimatePresence, motion } from 'framer-motion';
import { useRedux } from 'hooks/useRedux';
import { useTranslate } from 'hooks/useTranslate';
import React, { useState } from 'react';
import { framerFade } from 'utilities/FramerAnimations';
import Button from '../Button/Button';
import Modal from '../Modal/Modal';
import './EmojiModal.module.scss';

// =========================

// Staggered animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.25,
    },
  },
};

const emojiVariants = {
  hidden: {
    opacity: 0,
    y: 10,
    scale: 0.9,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.6,
      ease: 'easeOut',
    },
  },
};

export default function EmojiModal({
  setModalIsOpen,
  handleSave,
  initialEmotions = [],
}: {
  setModalIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleSave?: (emotions: EmotionsEntity[]) => void;
  initialEmotions?: string[]; // Array of emoji slugs that are already selected
}) {
  const translate = useTranslate();

  const emojis = useRedux((state) =>
    state.activity?.entities.allIds
      .map((id) => state.activity?.entities.byId[id])
      .filter((a) => a?.type === 'emotion')
      .sort((a, b) => (b?.order || 0) - (a?.order || 0))
  );

  // Initialize selected emotions based on initial props
  const [selectedEmotions, setSelectedEmotions] = useState<number[]>(
    initialEmotions
      .map((slug) => {
        const emoji = emojis?.find((e) => e?.slug === slug);
        return emoji?.id || 0;
      })
      .filter((id) => id !== 0)
  );

  const selected = (emojiId?: number) =>
    !!selectedEmotions.find((id) => id === emojiId);

  function handleClick(emojiId?: number) {
    if (!emojiId) return;

    if (selected(emojiId)) {
      // If already selected, remove it
      setSelectedEmotions((prev) => prev.filter((id) => id !== emojiId));
    } else {
      // If not selected, add it
      setSelectedEmotions((prev) => {
        const newSelection = [...prev, emojiId];

        // If we now have more than 2 emojis, remove the first one (oldest selection)
        if (newSelection.length > 2) {
          return newSelection.slice(1); // Remove the first element, keep the last 2
        }

        return newSelection;
      });
    }
  }

  function handleAdd() {
    if (handleSave) {
      // Convert selected emotion IDs to the format expected by handleSave
      const emotionsWithSlugs = selectedEmotions.map((emotionId) => {
        const emoji = emojis?.find((e) => e?.id === emotionId);
        return {
          slug: emoji?.slug || '',
        };
      });
      handleSave(emotionsWithSlugs);
    }
    setModalIsOpen(false);
  }

  return (
    <Modal setModalIsOpen={setModalIsOpen} size="small-medium">
      <div styleName="wrapper">
        <motion.div
          styleName="emojis"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {emojis?.map((emoji) => (
            <motion.div
              key={emoji?.id}
              styleName="emoji"
              onClick={() => handleClick(emoji?.id)}
              variants={emojiVariants}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              <img src={emoji?.thumbnail} alt={emoji?.name} />
              <AnimatePresence>
                {selected(emoji?.id) && (
                  <motion.div styleName="active" {...framerFade} />
                )}
              </AnimatePresence>
              <p style={{ opacity: selected(emoji?.id) ? 1 : '' }}>
                {emoji?.name}
              </p>
            </motion.div>
          ))}
        </motion.div>
        <div styleName="buttons ">
          <Button color="grey" onClick={() => setModalIsOpen(false)}>
            {translate('general.terms.cancel')}
          </Button>
          <Button onClick={handleAdd}>{translate('general.terms.add')}</Button>
        </div>
      </div>
    </Modal>
  );
}
